import { zodSsnValidation } from '@fruitful/fruitils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    BodyMediumRegular,
    ControlledInput,
    Form,
    H5Emphasized,
    isWeb,
    Private,
    View,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { formatWithMask, SSN_MASK } from '../../../../common/utils/maskUtils';
import type { RunKYCVerifyBody } from '../../../hooks/kyc/useRunKycVerify';
import KycButton from './KycButton';

const ssnSchema = z.object({
    ssn: zodSsnValidation,
});

type SsnSchema = z.infer<typeof ssnSchema>;

interface SsnProps {
    onPressNext: (formData?: Partial<RunKYCVerifyBody>) => void;
}

const Ssn = ({ onPressNext }: SsnProps) => {
    const [showSsn, setShowSsn] = useState(false);

    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<SsnSchema>({
        resolver: zodResolver(ssnSchema),
        defaultValues: {
            ssn: '',
        },
    });

    const onSubmit: SubmitHandler<SsnSchema> = ({ ssn }) => {
        // Keep formatting on ssn for synctera request to be successful
        onPressNext({ ssn });
    };

    return (
        <Private>
            <YStack
                flex={1}
                justifyContent={isWeb ? 'flex-start' : 'space-between'}
            >
                <View>
                    <H5Emphasized marginBottom={8}>
                        What&apos;s your Social Security{'\n'}number?
                    </H5Emphasized>
                    <BodyMediumRegular color="$gray900" marginBottom={24}>
                        We&apos;re required to collect this to verify your
                        identity.
                    </BodyMediumRegular>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <YStack gap={12}>
                            <ControlledInput
                                name="ssn"
                                type="numeric"
                                control={control}
                                register={register}
                                errors={errors}
                                secureTextEntry={!showSsn}
                                keyboardType="numbers-and-punctuation"
                                enterKeyHint="done"
                                enablesReturnKeyAutomatically={true}
                                onSubmitEditing={handleSubmit(onSubmit)}
                                onFocus={() => {
                                    setShowSsn(true);
                                }}
                                onEndEditing={() => {
                                    setShowSsn(false);
                                }}
                                formatValueOnChange={(_, value) => {
                                    const { masked } = formatWithMask(
                                        SSN_MASK,
                                        value,
                                    );
                                    return masked;
                                }}
                            />
                        </YStack>
                    </Form>
                </View>
                <KycButton
                    onPress={handleSubmit(onSubmit)}
                    disabled={!isValid}
                />
            </YStack>
        </Private>
    );
};

export default Ssn;
